import { Autocomplete, Avatar, Box, Button, Checkbox, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, Modal, Radio, RadioGroup, SelectChangeEvent, TextField, Tooltip, Typography, Grow } from '@mui/material';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import CustomTextField from '../FormFields/CustomTextField';
import CustomSelect, { IOptions } from '../FormFields/CustomSelect';
import GroupedCategorySelect from '../FormFields/GroupedCategorySelect';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';

import { ApiStatus } from "../../utils/api.types";
import { fetchBuyerOrSupplierByName, fetchAccounts } from '../../store/accountThunk';
import { fetchEmployees } from '../../store/employeeStore/employeeThunk';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { fetchCurrencies } from '../../store/currencyStore/currencyThunk';
import { recordExpensePayment, updateExpensePayment, fetchExpenseCategories } from '../../store/expenseStore/expenseThunk';
import { addDoc, deleteDoc } from '../../store/docAttachThunk';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { fetchTaxRates } from '../../store/gstStore/gstThunk';
import { fetchCostCentres } from '../../store/costCentreStore/costCentreThunk';
import InfoIcon from '@mui/icons-material/Info';
import moment from 'moment';
import { Utils } from '../../utils/common-utils';
import { deepOrange, deepPurple, green, pink, blue, teal } from "@mui/material/colors";
import { ExpenseFormDataNew } from '../../utils/expense.types';

interface DialogAddExpenseFormProps {
  title: String;
  open: boolean;
  onClose: (refresh: any) => void;
  editExpense?: any;
  isEmployeeUser?: boolean;
  loggedInEmployeeId?: string;
  loggedInEmployeeName?: string;
}

interface ITdsOption extends IOptions {
  rate?: number;
  taxSection?: string;
  taxName?: string;
}

const DialogAddExpenseForm: FC<DialogAddExpenseFormProps> = ({ onClose, open, title, editExpense, isEmployeeUser = false, loggedInEmployeeId, loggedInEmployeeName }) => {

  const { status } = useAppSelector(state => state.expense);
  const { categories } = useAppSelector(state => state.expense);
  const { selectedAccount, user } = useAppSelector(state => state.auth);
  const isAdminOrManager = selectedAccount?.owner?._id === user?._id || selectedAccount?.owner === user?._id || user?.role?.some((r: any) => ['admin', 'Admin', 'ADMIN', 'manager', 'Manager', 'MANAGER', 'Owner', 'owner', 'OWNER'].includes(r.role));
  const { costCentres } = useAppSelector(state => state.costCentres);
  const dispatch = useAppDispatch();

  // Form states
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [instrumentDate, setInstrumentDate] = useState<Date>(new Date());

  // Bank account states
  const [selectedToBankAccount, setSelectedToBankAccount] = useState('');
  const [selectedFromBankAccount, setSelectedFromBankAccount] = useState('');
  const [toBankAccountOptions, setToBankAccountOptions] = useState<IOptions[]>([]);
  const [fromBankAccountOptions, setFromBankAccountOptions] = useState<IOptions[]>([]);

  // Payment mode states
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [isCash, setIsCash] = useState(true);
  const [instrumentDisabled, setInstrumentDisabled] = useState(true);

  // Currency and vendor states
  const [currencyOptions, setCurrencyOptions] = useState<IOptions[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [paymentToOrFromAccount, setPaymentToOrFromAccount] = useState<any>();
  const [newOption, setNewOption] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [closedSelect, setClosedSelect] = useState(false);

  // Expense specific states
  const [selectedExpenseCategory, setSelectedExpenseCategory] = useState('');
  const [expenseStatus, setExpenseStatus] = useState('BOOKED');
  const [selectedCostCentre, setSelectedCostCentre] = useState('');

  const expenseStatusOptions: IOptions[] = isAdminOrManager
    ? [{ label: 'Booked', value: 'BOOKED' }, { label: 'Paid', value: 'PAID' }]
    : [{ label: 'Booked', value: 'BOOKED' }];

  // Slip file state
  const [slipFiles, setSlipFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [slipPreview, setSlipPreview] = useState<{ open: boolean; file: File | null }>({ open: false, file: null });

  // TDS states
  const [tdsTaxRateOptions, setTdsTaxRateOptions] = useState<ITdsOption[]>([]);
  const [tdsSelected, setTdsSelected] = useState({
    _id: "",
    label: "",
    rate: 0
  });
  const [tdsAmount, setTdsAmount] = useState<number>(0);
  const [netPayableAmount, setNetPayableAmount] = useState<number>(0);
  const [applyTds, setApplyTds] = useState<boolean>(false);

  // Form validation schema
  const schema = yup.object().shape({
    paymentFrom: yup.string().required("Vendor/Supplier is required."),
    paymentDate: yup.string().required("Payment date is required."),
    paymentMode: yup.string().required("Payment mode is required."),
    currency: yup.string().required("Currency is required."),
    amount: yup.string().required("Amount is required."),
    expenseCategory: yup.string().required("Expense category is required."),
  });

  const methods = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema)
  });
  const { control, register } = methods;

  // Load TDS rates once when form opens
  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const getTdsRates = async () => {
      try {
        const taxRatesData: any = await dispatch(fetchTaxRates()).unwrap();
        if (taxRatesData && taxRatesData.length > 0 && isMounted) {
          const tdsRates = taxRatesData.filter((item: any) => item.taxType === "tds");
          const tdsOptions = tdsRates.map((item: any) => ({
            label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
            value: item._id,
            rate: item.taxRate,
            taxSection: item.taxSection,
            taxName: item.taxName
          }));
          setTdsTaxRateOptions(tdsOptions);
        }
      } catch (error) {
        console.error('Error fetching TDS rates:', error);
      }
    };

    getTdsRates();

    return () => {
      isMounted = false;
    };
  }, [dispatch, open]);

  // Load currencies once when form opens
  useEffect(() => {
    if (!open || currencyOptions.length > 0) return;

    let isMounted = true;

    const getCurrencies = async () => {
      try {
        const data: any = await dispatch(fetchCurrencies());
        if (data.payload && isMounted) {
          setCurrencyOptions(data.payload.map((c: any) => ({
            label: c.code,
            value: c._id
          })));
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
      }
    };

    getCurrencies();

    return () => {
      isMounted = false;
    };
  }, [dispatch, open, currencyOptions.length]);

  // Load banks and cost centres when form opens and selectedAccount changes
  useEffect(() => {
    if (!open || !selectedAccount?._id) return;

    let isMounted = true;

    const loadAccountData = async () => {
      try {
        // Load bank accounts
        const bankData: any = await dispatch(fetchBanks(selectedAccount._id));
        if (bankData.payload?.banks?.length > 0 && isMounted) {
          const bankOptions = bankData.payload.banks.map((c: any) => ({
            label: c.accountName + '-' + c.bankName,
            value: c._id
          }));
          setFromBankAccountOptions(bankOptions);

          const firstBank = bankData.payload.banks[0];
          methods.setValue('fromBankAccount', firstBank._id);
          setSelectedFromBankAccount(firstBank._id);
        }

        // Load cost centres
        await dispatch(fetchCostCentres({ activeOnly: true }));
      } catch (error) {
        console.error('Error loading account data:', error);
      }
    };

    loadAccountData();

    return () => {
      isMounted = false;
    };
  }, [selectedAccount?._id, open, dispatch]);

  // Initialize form dates once when form opens
  useEffect(() => {
    if (!open) return;

    methods.setValue('paymentDate', new Date());
    methods.setValue('instrumentDate', new Date());

    // Pre-fill employee for employee users (only when not editing)
    if (isEmployeeUser && loggedInEmployeeId && !editExpense) {
      setPaymentToOrFromAccount({
        _id: loggedInEmployeeId,
        legalName: loggedInEmployeeName || '',
        name: loggedInEmployeeName || '',
        _type: 'employee'
      });
      methods.setValue('paymentFrom', loggedInEmployeeId);
    }

    // Pre-fill self account for admin/manager (not editing)
    if (isAdminOrManager && !isEmployeeUser && !editExpense && selectedAccount) {
      setPaymentToOrFromAccount({
        _id: selectedAccount._id,
        legalName: selectedAccount.legalName || selectedAccount.name || 'Account',
        name: selectedAccount.name || selectedAccount.legalName || 'Account',
        _type: 'account'
      });
      methods.setValue('paymentFrom', selectedAccount._id);
      setIsSelfAccount(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Pre-fill form when editing
  useEffect(() => {
    if (!open || !editExpense) return;

    const isEmp = editExpense.paymentFor === 'employee';
    const isAcct = editExpense.paymentFor === 'account';

    if (isAcct) {
      // Account expense — set self account
      setPaymentToOrFromAccount({
        _id: selectedAccount?._id,
        legalName: selectedAccount?.legalName || selectedAccount?.name || 'Account',
        name: selectedAccount?.name || selectedAccount?.legalName || 'Account',
        _type: 'account'
      });
      setIsSelfAccount(true);
    } else if (isEmp && editExpense.employee?._id) {
      setPaymentToOrFromAccount({
        _id: editExpense.employee._id,
        legalName: editExpense.employee.name,
        name: editExpense.employee.name,
        _type: 'employee'
      });
      setIsSelfAccount(false);
    } else if (editExpense.paymentTo?._id) {
      setPaymentToOrFromAccount({
        _id: editExpense.paymentTo._id,
        legalName: editExpense.paymentTo.legalName,
        name: editExpense.paymentTo.name,
        _type: 'vendor'
      });
      setIsSelfAccount(false);
    } else {
      // No party set — leave empty
      setPaymentToOrFromAccount(null);
      setIsSelfAccount(false);
    }
    methods.setValue('amount', editExpense.amount);
    methods.setValue('remark', editExpense.remarks || '');
    methods.setValue('instrumentId', editExpense.instrumentId || '');
    setSelectedExpenseCategory(editExpense.expenseCategory?._id || editExpense.expenseCategory || '');
    setPaymentMode(editExpense.paymentMode || 'CASH');
    setExpenseStatus(editExpense.paymentStatus === 'REJECTED' ? 'BOOKED' : editExpense.paymentStatus || 'BOOKED');
    setSelectedCostCentre(editExpense.costCentre?._id || editExpense.costCentre || '');
    setSelectedFromBankAccount(editExpense.fromBankAccount?._id || editExpense.fromBankAccount || '');
    setSelectedToBankAccount(editExpense.toBankAccount?._id || editExpense.toBankAccount || '');
    if (editExpense.paymentDate) {
      setPaymentDate(new Date(editExpense.paymentDate));
      methods.setValue('paymentDate', new Date(editExpense.paymentDate));
    }
    setExistingAttachments(editExpense.attachments || []);
  }, [open, editExpense]);

  // Memoize TDS options to prevent unnecessary re-renders
  const tdsOptionsWithNoTds = useMemo(() => {
    return [{ label: 'No TDS', value: '' }, ...tdsTaxRateOptions];
  }, [tdsTaxRateOptions]);

  // Handle payment mode change
  useEffect(() => {
    if (paymentMode === 'CASH') {
      setIsCash(true);
      setInstrumentDisabled(true);
      setSelectedToBankAccount('');
    } else {
      setIsCash(false);
      setInstrumentDisabled(false);
    }
  }, [paymentMode]);

  // Handle date changes
  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue && name) {
      if (name === 'paymentDate') {
        setPaymentDate(newValue);
      } else if (name === 'instrumentDate') {
        setInstrumentDate(newValue);
      }
      methods.setValue(name, newValue);
    }
  };

  // Handle vendor selection
  const onChangeHandler = (_: any, value: any) => {
    setNewOption('');
    setIsLoading(false);
    setPaymentToOrFromAccount(value);
    if (value && value._id) {
      methods.setValue('paymentFrom', value._id);
    } else {
      methods.setValue('paymentFrom', '');
    }
  };

  // Self account checkbox state
  const [isSelfAccount, setIsSelfAccount] = useState(false);
  const allAccounts = useAppSelector(state => state.account.accounts);

  // Fetch accounts when form opens (for account search in autocomplete)
  useEffect(() => {
    if (open && isAdminOrManager && !isEmployeeUser) {
      dispatch(fetchAccounts());
    }
  }, [open, isAdminOrManager, isEmployeeUser, dispatch]);

  // Handle self account checkbox
  const handleSelfAccountToggle = () => {
    if (!isSelfAccount) {
      // Check ON — set current account as selected party
      setPaymentToOrFromAccount({
        _id: selectedAccount?._id,
        legalName: selectedAccount?.legalName || selectedAccount?.name || 'Account',
        name: selectedAccount?.name || selectedAccount?.legalName || 'Account',
        _type: 'account'
      });
      methods.setValue('paymentFrom', selectedAccount?._id || '');
    } else {
      // Check OFF — clear selection
      setPaymentToOrFromAccount(null);
      methods.setValue('paymentFrom', '');
    }
    setIsSelfAccount(!isSelfAccount);
  };

  // Handle vendor + employee + account search
  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
    if (!value) {
      setOptions([]);
      return;
    }

    try {
      // Search vendors
      const queryString = `name=${value}&searchFor=supplier`;
      const vendorData = await dispatch(fetchBuyerOrSupplierByName(queryString)).unwrap();
      const vendorResults = (vendorData?.accounts || []).map((v: any) => ({
        ...v,
        _type: 'vendor'
      }));

      // Search employees
      let employeeResults: any[] = [];
      if (selectedAccount?._id) {
        try {
          const empData: any = await dispatch(fetchEmployees({
            accountId: selectedAccount._id,
            type: 'employee',
            params: `filter=${value}`
          })).unwrap();
          employeeResults = (empData?.employees || []).map((e: any) => ({
            _id: e._id,
            legalName: e.name || e.empId,
            name: e.name || e.empId,
            _type: 'employee'
          }));
        } catch (err) {}
      }

      // Search accounts (for admin/manager only)
      let accountResults: any[] = [];
      if (isAdminOrManager && !isEmployeeUser) {
        accountResults = (allAccounts || [])
          .filter((a: any) => {
            const name = (a.legalName || a.name || '').toLowerCase();
            return name.includes(value.toLowerCase());
          })
          .map((a: any) => ({
            _id: a._id,
            legalName: a.legalName || a.name,
            name: a.name || a.legalName,
            _type: 'account'
          }));
      }

      setOptions([...accountResults, ...vendorResults, ...employeeResults]);
    } catch (error) {
      console.error('Error searching vendors:', error);
    }
  };

  // Get random color for avatar
  const getRandomColor = () => {
    const colors = [pink[500], green[500], blue[500], deepOrange[500], deepPurple[500], teal[500]];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Render autocomplete option
  const handleRenderOption = (e: any, b: any, c: any) => {
    const isEmployee = b._type === 'employee';
    const isAccount = b._type === 'account';
    return <div {...e} className='auto-item' key={Math.random()} style={{alignItems: 'center'}}>
      <div className='img-wrapper' style={{marginLeft: 10}}>
        <Avatar
          alt={b.name}
          imgProps={{ referrerPolicy: "no-referrer" }}
          src={b.logo}
          sx={{bgcolor: isAccount ? teal[500] : isEmployee ? blue[500] : getRandomColor() }}
          style={{
            width: "35px",
            height: "35px",
            objectFit: "contain",
          }}
          sizes={"xs"}
        >{b.name?.toString().substr(0,1).toUpperCase()}</Avatar>
      </div>
      <div style={{marginLeft: 10}}>
        <b style={{'marginRight': '5px'}}>
          <p style={{fontSize:13}}>
            {b.legalName}
            {isAccount ? (
              <Chip
                size='small'
                style={{
                  height:18, float: 'right', borderRadius:7, color:'#00695c', backgroundColor:'#e0f2f1',
                  fontSize:11, marginTop:2, marginRight:3, marginLeft:3
                }}
                label="Account"
              />
            ) : isEmployee ? (
              <Chip
                size='small'
                style={{
                  height:18, float: 'right', borderRadius:7, color:'#1565c0', backgroundColor:'#e3f2fd',
                  fontSize:11, marginTop:2, marginRight:3, marginLeft:3
                }}
                label="Employee"
              />
            ) : b.isVendor ? (
              <Chip
                size='small'
                style={{
                  height:18, float: 'right', borderRadius:7,color:'#49a563',backgroundColor:'#edf6ef',
                  fontSize:11,marginTop:2,marginRight:3, marginLeft:3
                }}
                label="Vendor"
              />
            ) : null}
          </p>
        </b>
        {!isEmployee && !isAccount && !b.counterPartyOfAccount && <Tooltip title="Verified account"><InfoIcon style={{ fontSize: '12px', color: '#aaa' }}></InfoIcon></Tooltip>}
      </div>
    </div>
  };

  // Handle radio button change
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMode((event.target as HTMLInputElement).value);
  };

  // Calculate TDS amount
  const calculateTds = (amount: number, tdsRate: number) => {
    if (!amount || !tdsRate) return 0;
    return Number((tdsRate * amount) / 100);
  };

  // Handle TDS selection change
  const handleTdsChange = (event: SelectChangeEvent<any>) => {
    const selectedTdsId = event.target.value;

    if (!selectedTdsId) {
      // Clear TDS
      setApplyTds(false);
      setTdsSelected({ _id: "", label: "", rate: 0 });
      setTdsAmount(0);
      const amount = parseFloat(methods.getValues('amount')) || 0;
      setNetPayableAmount(amount);
      return;
    }

    const selectedTdsOption = tdsTaxRateOptions.find((opt: ITdsOption) => opt.value === selectedTdsId);
    if (selectedTdsOption && selectedTdsOption.rate) {
      setApplyTds(true);
      setTdsSelected({
        _id: selectedTdsOption.value,
        label: selectedTdsOption.label,
        rate: selectedTdsOption.rate
      });

      // Calculate TDS amount
      const amount = parseFloat(methods.getValues('amount')) || 0;
      const calculatedTds = calculateTds(amount, selectedTdsOption.rate);
      setTdsAmount(calculatedTds);
      setNetPayableAmount(amount - calculatedTds);
    }
  };

  // Recalculate TDS when amount changes
  const handleAmountChange = (event: any) => {
    const amount = parseFloat(event.target.value) || 0;

    if (applyTds && tdsSelected.rate > 0) {
      const calculatedTds = calculateTds(amount, tdsSelected.rate);
      setTdsAmount(calculatedTds);
      setNetPayableAmount(amount - calculatedTds);
    } else {
      setNetPayableAmount(amount);
    }
  };

  // Handle form submission
  const onSubmit = async () => {
    const data = methods.getValues();

    try {
      setFormSubmitted(true);

      const amount = parseFloat(data.amount) || 0;
      const isEmployee = paymentToOrFromAccount?._type === 'employee';
      const isAccount = isSelfAccount || paymentToOrFromAccount?._type === 'account';

      const addPaymentReqObj: ExpenseFormDataNew = {
        "account": isAccount ? (paymentToOrFromAccount?._id || selectedAccount._id) : selectedAccount._id,
        "paymentTo": (isEmployee || isAccount) ? undefined : paymentToOrFromAccount?._id,
        "employee": isEmployee ? paymentToOrFromAccount?._id : undefined,
        "paymentFor": isAccount ? 'account' : isEmployee ? 'employee' : 'vendor',
        "expenseCategory": selectedExpenseCategory,
        "paymentDate": new Date(paymentDate).getTime(),
        "amount": amount,
        "costCentre": selectedCostCentre || undefined,
        "currency": "62a4b0ceb628fb10ac97ce0e",
        "paymentStatus": expenseStatus,
        "toBankAccount": selectedToBankAccount || undefined,
        "paymentMode": paymentMode.toUpperCase(),
        "instrument": paymentMode.toUpperCase(),
        "instrumentId": data.instrumentId,
        "instrumentDate": new Date(paymentDate).getTime(),
        "fromBankAccount": selectedFromBankAccount,
        "remarks": data.remark,
        "tdsSelected": applyTds && tdsSelected._id ? {
          apply: true,
          totalAmount: tdsAmount.toFixed(2),
          taxableAmount: amount,
          displayType: 0,
          tdsSelected: {
            _id: tdsSelected._id,
            label: tdsSelected.label,
            rate: tdsSelected.rate
          }
        } : {
          apply: false,
          totalAmount: "0.00",
          taxableAmount: 0,
          displayType: 0,
          tdsSelected: { _id: "", label: "", rate: 0 }
        }
      };

      let savedExpense: any;
      if (editExpense?._id) {
        savedExpense = await dispatch(updateExpensePayment({ expenseId: editExpense._id, payload: addPaymentReqObj })).unwrap();
      } else {
        savedExpense = await dispatch(recordExpensePayment(addPaymentReqObj)).unwrap();
      }

      // Upload slip files if any
      if (slipFiles.length > 0 && savedExpense?._id) {
        for (const file of slipFiles) {
          const form = new FormData();
          form.append('collectionName', 'ExpensePayment');
          form.append('collectionRefId', savedExpense._id);
          form.append('documentName', file.name);
          form.append('attachment', file);
          try {
            await dispatch(addDoc(form as any)).unwrap();
          } catch (err) {
            console.error('Slip upload failed:', err);
          }
        }
      }

      // Reset form
      methods.reset();
      // For employee users, keep the employee pre-selected
      if (isEmployeeUser && loggedInEmployeeId) {
        setPaymentToOrFromAccount({
          _id: loggedInEmployeeId,
          legalName: loggedInEmployeeName || '',
          name: loggedInEmployeeName || '',
          _type: 'employee'
        });
      } else {
        setPaymentToOrFromAccount(null);
      }
      setSelectedExpenseCategory('');
      setExpenseStatus('BOOKED');
      setPaymentMode('CASH');
      setIsSelfAccount(false);
      setApplyTds(false);
      setTdsSelected({ _id: "", label: "", rate: 0 });
      setTdsAmount(0);
      setNetPayableAmount(0);
      setSlipFiles([]);
      setExistingAttachments([]);

      onClose(true);
    } catch (error: any) {
      console.error('Error creating expense payment:', error);
    } finally {
      setFormSubmitted(false);
    }
  };

  // Handle cancel
  const handleClose = () => {
    methods.reset();
    setPaymentToOrFromAccount(null);
    setSelectedExpenseCategory('');
    setExpenseStatus('BOOKED');
    setPaymentMode('CASH');
    setIsSelfAccount(false);
    setApplyTds(false);
    setTdsSelected({ _id: "", label: "", rate: 0 });
    setTdsAmount(0);
    setNetPayableAmount(0);
    setSlipFiles([]);
    onClose(false);
  };

  return (
      <div className="drawer-form-container"  style={{width: "100%", maxWidth: "800px"}}>
        <FormProvider {...methods}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              padding: "2px 0 0 2px",
              position: "relative"
            }}
            component="form"
            noValidate>
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: '40px' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                  {title}
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent sx={{
              padding: '24px',
              paddingBottom: '100px',
              overflowY: 'auto',
              flex: 1
            }}>
              <Grid container spacing={2}>
                {/* Reference ID - show for existing expenses */}
                {(editExpense?.referenceId || editExpense?.txnId) && (
                  <Grid item xs={12} md={4}>
                    <CustomTextField
                      value={editExpense.referenceId || `EXP-${String(editExpense.txnId).padStart(3, '0')}`}
                      name="referenceId"
                      label="Reference ID"
                      size="small"
                      disabled
                      fullWidth
                      InputProps={{
                        style: { fontWeight: 600, color: '#6366f1' }
                      }}
                    />
                  </Grid>
                )}

                {/* Vendor/Party/Employee/Account field */}
                <Grid item xs={12} md={8}>
                  <Controller
                    name={'paymentTo'}
                    control={control}
                    render={() => (
                      isEmployeeUser ? (
                        <CustomTextField
                          value={loggedInEmployeeName || ''}
                          name="paymentFrom"
                          label={'Employee *'}
                          size="small"
                          disabled
                          style={{ width: '100%' }}
                        />
                      ) : isSelfAccount ? (
                        <CustomTextField
                          value={paymentToOrFromAccount?.legalName || paymentToOrFromAccount?.name || selectedAccount?.legalName || selectedAccount?.name || ''}
                          name="paymentFrom"
                          label={'Vendor / Employee / Account *'}
                          size="small"
                          disabled
                          style={{ width: '100%' }}
                        />
                      ) : (
                      <Autocomplete
                        id={'paymentTo'}
                        freeSolo
                        value={paymentToOrFromAccount ?? ""}
                        onChange={(_, value) => {
                          onChangeHandler(_, value);
                          if (value && value._type === 'account') {
                            setIsSelfAccount(true);
                          } else {
                            setIsSelfAccount(false);
                          }
                        }}
                        onInputChange={onInputChangeHandler}
                        renderOption={handleRenderOption}
                        options={options}
                        getOptionLabel={option => option.legalName || option}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            value={paymentToOrFromAccount ?? ""}
                            name="paymentFrom"
                            label={'Vendor / Employee / Account *'}
                            size="small"
                          />
                        )}
                        style={{ width: '100%' }}
                      />
                      )
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Amount *"
                    name="amount"
                    type="Number"
                    variant="outlined"
                    size='small'
                    fullWidth
                    onChange={handleAmountChange}
                  />
                </Grid>

                {/* Self Account checkbox - admin/manager only */}
                {isAdminOrManager && !isEmployeeUser && (
                  <Grid item xs={12} sx={{ pt: '0px !important', pb: 0, mt: -0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelfAccount}
                          onChange={handleSelfAccountToggle}
                          size="small"
                          sx={{ py: 0, color: '#00695c', '&.Mui-checked': { color: '#00695c' } }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: isSelfAccount ? '#00695c' : '#666' }}>
                          Self Account
                        </Typography>
                      }
                      sx={{ ml: 0, mb: 0 }}
                    />
                  </Grid>
                )}

                {/* Category */}
                <Grid item xs={6}>
                  <GroupedCategorySelect
                    name="expenseCategory"
                    label="Category *"
                    id="expenseCategory"
                    size="small"
                    categories={categories || []}
                    selectedValue={selectedExpenseCategory}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setSelectedExpenseCategory(event.target.value);
                    }}
                    showNoneLabel
                    required
                    allowCreate={true}
                    onCategoryCreated={(newCategory) => {
                      dispatch(fetchExpenseCategories({ flat: false }));
                      setSelectedExpenseCategory(newCategory._id);
                    }}
                  />
                </Grid>

                {/* Cost Centre */}
                <Grid item xs={6}>
                  <CustomSelect
                    name="costCentre"
                    id="costCentre"
                    label="Cost Centre (Optional)"
                    size="small"
                    fullWidth
                    options={costCentres.map((cc: any) => ({
                      label: cc.alias ? `${cc.name} (${cc.alias})` : cc.name,
                      value: cc._id
                    }))}
                    value={selectedCostCentre}
                    onChange={(event: SelectChangeEvent<any>) => setSelectedCostCentre(event.target.value)}
                  />
                </Grid>

                {/* TDS Selection */}
                <Grid item xs={12}  md={8} mt={2}>
                  <CustomSelect
                    name="tdsSelected"
                    label="Apply TDS (Optional)"
                    id="tdsSelected"
                    size="small"
                    fullWidth
                    options={tdsOptionsWithNoTds}
                    value={tdsSelected._id || ""}
                    onChange={handleTdsChange}
                  />
                </Grid>

                {/* TDS Amount Display */}
                {applyTds && tdsAmount > 0 && (
                  <Grid item xs={12}  md={8}>
                    <Box
                      sx={{
                        p: 1.5,
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #86efac',
                        borderRadius: '6px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#16a34a',
                          fontWeight: 500,
                          mb: 0.5
                        }}
                      >
                        TDS Deducted ({tdsSelected.rate}%)
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '1rem',
                          color: '#15803d',
                          fontWeight: 600
                        }}
                      >
                        {Utils.formatNumber(tdsAmount.toFixed(2))}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: '#16a34a',
                          mt: 0.5
                        }}
                      >
                        Net Payable: {Utils.formatNumber(netPayableAmount.toFixed(2))}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Date */}
                <Grid item xs={12} md={4} mt={2}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      label="Date"
                      inputFormat="dd/MM/yyyy"
                      maxDate={moment().add(1).toDate()}
                      value={paymentDate}
                      onChange={(value) => { handleDateChange(value, 'paymentDate') }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name={'paymentDate'}
                          required
                          variant="outlined"
                          size='small'
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* From Account */}
                <Grid item xs={12} md={6} mt={2}>
                  <CustomSelect
                    sx={{
                      width: '100%',
                      '& .MuiInputLabel-root': {
                        transform: 'translate(14px, 9px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)'
                        }
                      },
                      '& .MuiSelect-select': {
                        padding: '8.5px 14px',
                      }
                    }}
                    name="fromBankAccount"
                    label="From Account"
                    id="fromBankAccount-1"
                    variant="outlined"
                    size='small'
                    options={fromBankAccountOptions}
                    value={selectedFromBankAccount ?? ""}
                    onChange={(event: SelectChangeEvent<any>) => {
                      methods.setValue('fromBankAccount', event.target.value);
                      setSelectedFromBankAccount(event.target.value);
                    }}
                  />
                </Grid>

                {/* To Account */}
                <Grid item xs={12} md={6} mt={2}>
                  <CustomSelect
                    disabled={isCash}
                    sx={{
                      width: '100%',
                      '& .MuiInputLabel-root': {
                        transform: 'translate(14px, 9px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)'
                        }
                      },
                      '& .MuiSelect-select': {
                        padding: '8.5px 14px',
                      }
                    }}
                    name="depositTo"
                    label="To Account"
                    id="depositTo"
                    variant="outlined"
                    size='small'
                    options={toBankAccountOptions}
                    value={selectedToBankAccount ?? ""}
                    onChange={(event: SelectChangeEvent<any>) => {
                      methods.setValue('toBankAccount', event.target.value);
                      setSelectedToBankAccount(event.target.value);
                    }}
                  />
                </Grid>

                {/* Payment Mode Radio Group */}
                <Grid item xs={12}>
                  <RadioGroup
                    {...methods.register("instrumentType")}
                    row
                    aria-labelledby="payment-mode-label"
                    defaultValue="CASH"
                    name="instrumentType"
                    onChange={handleRadioChange}
                    sx={{
                      '& .MuiFormControlLabel-root': {
                        mr: 2,
                        ml: 0,
                        '& .MuiRadio-root': {
                          p: 0.5,
                          mr: 0.5,
                          color: '#6366f1',
                          '&.Mui-checked': {
                            color: '#6366f1',
                          }
                        },
                        '& .MuiTypography-root': {
                          fontSize: '0.875rem'
                        }
                      }
                    }}
                  >
                    <FormControlLabel value="CASH" control={<Radio size="small" />} label="Cash" />
                    <FormControlLabel value="CHEQUE" control={<Radio size="small" />} label="Cheque" />
                    <FormControlLabel value="CARD" control={<Radio size="small" />} label="Card" />
                    <FormControlLabel value="UPI" control={<Radio size="small" />} label="UPI" />
                    <FormControlLabel value="NETBANKING" control={<Radio size="small" />} label="Net Banking" />
                  </RadioGroup>
                </Grid>

                {/* Transaction Reference */}
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    disabled={isCash}
                    variant="outlined"
                    size='small'
                    fullWidth
                    label={paymentMode === 'CHEQUE' ? "Cheque Number" :
                           paymentMode === 'UPI' ? "UPI ID" :
                           paymentMode === 'CARD' ? "Card Ref" :
                           "Transaction Ref"}
                    name="instrumentId"
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12} md={6} mt={2}>
                  <CustomSelect
                    name="paymentStatus"
                    label="Status *"
                    id="paymentStatus"
                    size="small"
                    options={expenseStatusOptions}
                    value={expenseStatus}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setExpenseStatus(event.target.value);
                    }}
                  />
                </Grid>

                {/* Remarks */}
                <Grid item xs={12}>
                  <CustomTextField
                    label="Remarks"
                    name="remark"
                    variant="outlined"
                    size='small'
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>

                {/* Slip Upload */}
                <Grid item xs={12} mt={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151' }}>
                    Attach Slip / Receipt
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {/* Existing attachments (edit mode) */}
                    {existingAttachments.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {existingAttachments.map((att: any, idx: number) => (
                          <Box key={att._id} sx={{ position: 'relative', border: '1px solid #e0e0e0', borderRadius: '6px', p: 0.5, display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#fafafa' }}>
                            {att.mimeType === 'application/pdf' || att.originalname?.toLowerCase().endsWith('.pdf') ? (
                              <PictureAsPdfIcon sx={{ fontSize: 36, color: '#ef4444' }} />
                            ) : (
                              <img src={att.url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                            )}
                            <Typography variant="caption" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {att.originalname || att.attachmentLabel || 'Slip'}
                            </Typography>
                            <IconButton size="small" onClick={async () => {
                              try {
                                await dispatch(deleteDoc({ id: att._id, collectionName: 'ExpensePayment', collectionRefId: editExpense?._id })).unwrap();
                                setExistingAttachments(prev => prev.filter(a => a._id !== att._id));
                              } catch (err) { console.error('Delete failed:', err); }
                            }} sx={{ p: 0.2 }}>
                              <CancelIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {(slipFiles.length + existingAttachments.length) < 3 && (
                      <label>
                        <Button
                          component="span"
                          variant="outlined"
                          size="small"
                          startIcon={<CloudUploadIcon />}
                          sx={{
                            borderStyle: 'dashed',
                            textTransform: 'none',
                            fontSize: '0.8rem',
                          }}
                        >
                          Upload Slip
                        </Button>
                        <input
                          type="file"
                          multiple
                          accept="image/*,.png,.jpg,.jpeg,.pdf"
                          onChange={(e) => {
                            if (!e.target.files) return;
                            setSlipFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                            e.target.value = '';
                          }}
                          style={{ display: 'none' }}
                        />
                      </label>
                    )}
                    {slipFiles.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {slipFiles.map((file, idx) => (
                          <Box key={idx} sx={{ position: 'relative', border: '1px solid #e0e0e0', borderRadius: '6px', p: 0.5, display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#fafafa', cursor: 'pointer', '&:hover': { borderColor: '#6366f1', boxShadow: '0 0 0 1px #6366f1' } }} onClick={() => setSlipPreview({ open: true, file })}>
                            {file.type.startsWith('image/') ? (
                              <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} />
                            ) : (
                              <PictureAsPdfIcon sx={{ fontSize: 40, color: '#ef4444' }} />
                            )}
                            <Typography variant="caption" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </Typography>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSlipFiles(prev => prev.filter((_, i) => i !== idx)); }} sx={{ p: 0.2 }}>
                              <CancelIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>

              </Grid>
            </DialogContent>

            <DialogActions sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              borderTop: '1px solid #c1c5cb',
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
              padding: '16px 24px',
              margin: 0,
              zIndex: 1000
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                width: '100%'
              }}>
                <Button
                  variant='outlined'
                  onClick={handleClose}
                  sx={{
                    minWidth: '100px',
                    height: '40px'
                  }}
                >
                  Cancel
                </Button>

                <LoadingButton
                  type="button"
                  onClick={onSubmit}
                  variant="contained"
                  disabled={!isSelfAccount && !paymentToOrFromAccount}
                  loading={status === ApiStatus.LOADING}
                  sx={{
                    minWidth: '140px',
                    height: '40px'
                  }}
                >
                  {editExpense ? 'Update Expense' : 'Record Expense'}
                </LoadingButton>
              </Box>
            </DialogActions>
          </Box>
        </FormProvider>

        {/* Slip Preview - Container Transform */}
        <Modal
          open={slipPreview.open}
          onClose={() => setSlipPreview({ open: false, file: null })}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Grow in={slipPreview.open} timeout={350} style={{ transformOrigin: 'center center' }}>
            <Box sx={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: 24,
              maxWidth: '80vw',
              maxHeight: '85vh',
              overflow: 'hidden',
              outline: 'none',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid #eee' }}>
                <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ maxWidth: 400 }}>{slipPreview.file?.name}</Typography>
                <IconButton size="small" onClick={() => setSlipPreview({ open: false, file: null })}>
                  <CancelIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
              <Box sx={{ p: 2, textAlign: 'center', overflow: 'auto', maxHeight: '75vh' }}>
                {slipPreview.file && (
                  slipPreview.file.type === 'application/pdf' ? (
                    <iframe
                      src={URL.createObjectURL(slipPreview.file)}
                      width="100%"
                      height="500px"
                      title="PDF Preview"
                      style={{ border: 'none', borderRadius: 4 }}
                    />
                  ) : (
                    <img
                      src={URL.createObjectURL(slipPreview.file)}
                      alt={slipPreview.file.name}
                      style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }}
                    />
                  )
                )}
              </Box>
            </Box>
          </Grow>
        </Modal>
      </div>
  );
};

export default DialogAddExpenseForm;
